import { motion } from "framer-motion";
import { useMemo, useState } from "react";
import Autoplay from "embla-carousel-autoplay";
import { Carousel, CarouselContent, CarouselItem, CarouselPrevious, CarouselNext } from "../ui/carousel";
import { BACKEND_URL } from "../../lib/api";

function resolveImageUrl(u) {
  if (!u) return "";
  if (u.startsWith("http://") || u.startsWith("https://")) return u;
  if (u.startsWith("/api/")) return `${BACKEND_URL}${u}`;
  return u;
}

export default function Portfolio({ items }) {
  const categories = useMemo(() => ["All", ...Array.from(new Set(items.map((i) => i.category)))], [items]);
  const [active, setActive] = useState("All");
  const filtered = active === "All" ? items : items.filter((i) => i.category === active);

  return (
    <section id="portfolio" className="py-10 lg:py-14 bg-white">
      <div className="container-x">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div className="max-w-2xl">
            <div className="overline text-brand-green">Portfolio</div>
            <h2 className="mt-3 font-display text-4xl sm:text-5xl font-extrabold text-brand-ink leading-tight tracking-tight">
              Recent work that <span className="text-brand-blue">turns heads.</span>
            </h2>
          </div>
          <div className="flex flex-wrap gap-2">
            {categories.map((c) => (
              <button
                key={c}
                onClick={() => setActive(c)}
                data-testid={`portfolio-filter-${c.toLowerCase()}`}
                className={`px-4 py-2 rounded-full border text-sm font-semibold transition ${
                  active === c
                    ? "bg-brand-ink text-white border-brand-ink"
                    : "bg-white text-gray-600 border-gray-200 hover:border-brand-blue hover:text-brand-blue"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-12 relative">
          <Carousel
            opts={{ align: "start", loop: true }}
            plugins={[Autoplay({ delay: 3000, stopOnInteraction: false, stopOnMouseEnter: true })]}
            className="w-full"
          >
            <CarouselContent className="-ml-4">
              {filtered.map((p, i) => (
                <CarouselItem key={p.id} className="pl-4 basis-1/2 sm:basis-1/3 lg:basis-1/4">
                  <motion.div
                    initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                    transition={{ duration: 0.45 }}
                    className="relative aspect-square overflow-hidden rounded-3xl border border-gray-200 group cursor-pointer shadow-sm hover:shadow-xl transition-shadow"
                    data-testid={`portfolio-item-${i}`}
                  >
                    <img
                      src={resolveImageUrl(p.image_url)}
                      alt={p.title}
                      className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-brand-ink/85 via-brand-ink/10 to-transparent opacity-90" />
                    <div className="absolute inset-x-0 bottom-0 p-5 text-white">
                      <div className="overline text-brand-green text-[10px]">{p.category}</div>
                      <h3 className="mt-1 font-display font-bold text-base sm:text-lg leading-tight line-clamp-2">{p.title}</h3>
                    </div>
                  </motion.div>
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselPrevious className="hidden sm:flex -left-4 lg:-left-6 bg-white border-brand-blue text-brand-blue hover:bg-brand-blue hover:text-white" />
            <CarouselNext className="hidden sm:flex -right-4 lg:-right-6 bg-white border-brand-blue text-brand-blue hover:bg-brand-blue hover:text-white" />
          </Carousel>
          <p className="mt-6 text-center text-xs text-gray-500 sm:hidden">Swipe to explore →</p>
        </div>
      </div>
    </section>
  );
}
